import {Button, colors, Form, Icon, IconType, List, Panel, Spinner} from 'nf-ui'
import SvgCheck from 'nf-ui/Icons/Check'
import SvgCog from 'nf-ui/Icons/Cog'
import SvgEditThin from 'nf-ui/Icons/EditThin'
import SvgSettingsRegular from 'nf-ui/Icons/SettingsRegular'
import SvgSwitcher from 'nf-ui/Icons/Switcher'
import React, {useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {SortField} from '~/apollo/queries/DataEditValue'
import {PanelLayout, PanelLayoutButtons} from '~/components/PanelLayout'
import {PanelHeader, usePanelNavigator} from '~/components/PanelNavigator'
import {PANEL_WIDTH} from '~/constants'

const HeaderIcon = styled.div`
    position: absolute;
    display: none;
    padding: 3px 3px;
    width: 24px;
    height: 24px;
    cursor: pointer;
    justify-content: space-between;
    align-items: center;
`

const HeaderMenuRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const MenuRowItemContainer = styled.div`
    display: flex;
    > span {
        margin: -4px 0px -2px;
        padding: 6px 6px 0px;
    }
`

export interface ManageColumnProps {
    hoverColumn: number
    otherMouseGesture: {buttons: number; type: string} | undefined
    columns: {idStr: string; name: string; type: string; subType?: string | null; isSelected: boolean}[]
    getCellBounds: (col: number, row: number) => {x: number; y: number; width: number; height: number} | undefined
    updateFieldName: (col: number, value: string) => Promise<void>
    updateFieldType: (col: number, type: string, subType: string) => Promise<void>
    sortField: SortField | undefined
    setSortField: (value: SortField) => void
}

interface MenuState {
    activeColumn: number
    selectedOption?: 'rename' | 'changeFieldType'
    actions: {id: string; label: string; icon?: IconType}[]
    checkedAction?: string
    busyAction?: string
    draftFieldName?: string
}

export const ManageColumn = ({
    hoverColumn,
    otherMouseGesture,
    columns,
    getCellBounds,
    updateFieldType,
    updateFieldName,
    sortField,
    setSortField,
}: ManageColumnProps) => {
    const headerMenuButtonRef = useRef<HTMLDivElement>(null)
    const {openPanel, panelProps, closePanel} = usePanelNavigator('headerMenu')

    const mainActions = [
        {
            id: 'rename',
            label: 'Rename',
            icon: SvgEditThin,
        },
        {
            id: 'changeFieldType',
            label: 'Change field type',
            icon: SvgSettingsRegular,
        },
    ]

    const changeFieldTypeActions = [
        {
            id: 'text',
            label: 'Text',
        },
        {
            id: 'category',
            label: 'Category',
        },
        {
            id: 'firstName',
            label: 'First Name',
        },
        {
            id: 'lastName',
            label: 'Last Name',
        },
        {
            id: 'email.giveAccess',
            label: 'Email (Give Access)',
        },
        {
            id: 'email.noAccess',
            label: 'Email (No Access)',
        },
    ]

    const [menuState, setMenuState] = useState<MenuState>({
        activeColumn: hoverColumn,
        actions: mainActions,
    })

    const moveHeaderButtons = () => {
        const sortElement = document.getElementById('headerSortButton')
        const menuElement = document.getElementById('headerMenuButton')
        if (!menuElement || !sortElement) return
        const column = panelProps.open ? menuState.activeColumn : hoverColumn
        if (column < 0) {
            sortElement.style['display'] = 'none'
            menuElement.style['display'] = 'none'
            return
        }
        const bounds = getCellBounds(column, -1)
        if (!bounds) {
            sortElement.style['display'] = 'none'
            menuElement.style['display'] = 'none'
            return
        }

        sortElement.style['top'] = `${bounds.y + 3}px`
        sortElement.style['left'] = `${bounds.x + bounds.width - 300}px`
        sortElement.style['display'] = 'flex'

        menuElement.style['top'] = `${bounds.y + 3}px`
        menuElement.style['left'] = `${bounds.x + bounds.width - 280}px`
        menuElement.style['display'] = 'flex'
    }

    const dismissPopup = () => {
        setMenuState({
            activeColumn: hoverColumn,
            actions: mainActions,
        })
        closePanel()
    }

    const handleAction = async (id: string) => {
        if (id === 'changeFieldType') {
            const column = columns[menuState.activeColumn]
            setMenuState({
                ...menuState,
                selectedOption: 'changeFieldType',
                actions: changeFieldTypeActions,
                checkedAction: column.subType
                    ? `${column.type}.${column.subType}`
                    : column.type === 'email'
                    ? 'email.noAccess'
                    : column.type,
            })
        } else if (menuState.selectedOption === 'changeFieldType') {
            setMenuState({
                ...menuState,
                busyAction: id,
            })
            const type = id.split('.')[0]
            const subType = id.split('.')[1]
            await updateFieldType(menuState.activeColumn, type, subType)
            dismissPopup()
        } else if (id === 'rename') {
            setMenuState({
                ...menuState,
                selectedOption: 'rename',
                draftFieldName: undefined,
            })
        } else if (id === 'completeRename' && menuState.draftFieldName) {
            setMenuState({
                ...menuState,
                busyAction: 'rename',
            })
            await updateFieldName(menuState.activeColumn, menuState.draftFieldName)
            dismissPopup()
        }
    }

    const openMenu = () => {
        setMenuState({
            ...menuState,
            actions: mainActions,
            checkedAction: undefined,
            selectedOption: undefined,
        })
        openPanel('headerMenu', {itemId: 'headerMenu'})
    }

    const updateFieldSorting = () => {
        const field = columns[menuState.activeColumn]
        setSortField({
            idStr: field.idStr,
            direction: sortField?.idStr === field.idStr && sortField?.direction !== 'desc' ? 'desc' : 'asc',
        })
    }

    const lastHoverColumn = useRef(-1)
    useEffect(() => {
        if (hoverColumn === lastHoverColumn.current) return
        lastHoverColumn.current = hoverColumn
        if (!panelProps.open) {
            moveHeaderButtons()
            if (hoverColumn >= 0) {
                setMenuState({
                    ...menuState,
                    activeColumn: hoverColumn,
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hoverColumn, panelProps])

    useEffect(() => {
        moveHeaderButtons()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panelProps])

    useEffect(() => {
        if (hoverColumn < 0 && !panelProps.open) return
        if (otherMouseGesture?.buttons) {
            moveHeaderButtons()
        }
        if (otherMouseGesture?.type === 'wheel') {
            moveHeaderButtons()
            for (let t = 10; t <= 1000; t += 10) setTimeout(() => moveHeaderButtons(), t)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otherMouseGesture])

    const isSelected = columns[menuState.activeColumn]?.isSelected
    return (
        <>
            <HeaderIcon id="headerSortButton" onClick={updateFieldSorting}>
                <SvgSwitcher
                    width={20}
                    height={20}
                    color={isSelected ? '#FFFFFF' : colors.primary['100']}
                ></SvgSwitcher>
            </HeaderIcon>
            <HeaderIcon id="headerMenuButton" ref={headerMenuButtonRef} onClick={openMenu}>
                <SvgCog width={24} height={24} color={isSelected ? '#FFFFFF' : colors.primary['100']}></SvgCog>
            </HeaderIcon>
            <Panel targetRef={headerMenuButtonRef} {...panelProps} onClose={() => dismissPopup()}>
                <PanelLayout key="headerMenu">
                    {menuState.selectedOption === 'rename' ? (
                        <>
                            <PanelHeader panel="rename">Rename this column</PanelHeader>
                            <Form.Group name="rename">
                                <Form.Input
                                    autoFocus={true}
                                    value={
                                        menuState.draftFieldName === undefined
                                            ? columns[menuState.activeColumn].name
                                            : menuState.draftFieldName
                                    }
                                    onChange={(value) => setMenuState({...menuState, draftFieldName: value})}
                                    onKeyUp={(key) => key.code === 'Enter' && handleAction('completeRename')}
                                />
                            </Form.Group>
                            <PanelLayoutButtons>
                                <Button
                                    variant="tertiary"
                                    disabled={!menuState.draftFieldName}
                                    loading={menuState.busyAction === 'rename'}
                                    onClick={() => handleAction('completeRename')}
                                >
                                    Rename
                                </Button>
                            </PanelLayoutButtons>
                        </>
                    ) : (
                        <List
                            width={PANEL_WIDTH}
                            rows={menuState.actions}
                            renderRow={(action) => (
                                <HeaderMenuRow onClick={() => handleAction(action.id)}>
                                    <MenuRowItemContainer>
                                        {action.label}
                                        {menuState.checkedAction === action.id && (
                                            <span>
                                                <SvgCheck className="check"></SvgCheck>
                                            </span>
                                        )}
                                        {menuState.busyAction === action.id && (
                                            <span>
                                                <Spinner></Spinner>
                                            </span>
                                        )}
                                    </MenuRowItemContainer>
                                    {action.icon && <Icon size={16} icon={action.icon} />}
                                </HeaderMenuRow>
                            )}
                        />
                    )}
                </PanelLayout>
            </Panel>
        </>
    )
}
